<template>
  <v-row>
    <v-col cols="12">
      <!-- judul + aksi -->
      <div class="bm-page-head">
        <router-link to="/report/budget-monitoring" class="bm-back">
          <v-icon small color="grey darken-1">mdi-chevron-left</v-icon>Budget Monitoring
        </router-link>
        <span class="grey--text">/</span>
        <div class="bm-title">PO {{ summary.poNumber || "#" + crmPoId }}</div>
        <div v-if="summary.title && summary.title !== summary.poNumber" class="bm-subtitle">
          {{ summary.title }}
        </div>
        <v-spacer></v-spacer>
        <v-btn
          outlined
          color="primary"
          :loading="recomputing"
          :disabled="loading"
          @click="confirmRecompute = true"
        >
          <v-icon left>mdi-refresh</v-icon>Hitung ulang PO ini
        </v-btn>
        <v-btn color="success" :loading="exporting" :disabled="!detail" @click="exportExcel">
          <v-icon left>mdi-file-excel</v-icon>Ekspor Excel
        </v-btn>
      </div>

      <v-skeleton-loader v-if="loading && !detail" type="card, article, table"></v-skeleton-loader>
      <empty-state
        v-else-if="!detail"
        icon="mdi-file-question-outline"
        title="PO belum pernah dihitung"
        text="PO ini belum ada di hasil perhitungan. Jalankan 'Hitung ulang PO ini' atau tunggu perhitungan otomatis berikutnya."
      >
        <v-btn color="primary" class="mt-2" @click="confirmRecompute = true"
          >Hitung ulang PO ini</v-btn
        >
      </empty-state>

      <template v-else>
        <!-- identitas + verdict -->
        <v-card class="pa-5 mb-4">
          <v-row align="center">
            <v-col cols="12" md="8">
              <v-row dense>
                <v-col cols="6">
                  <div class="bm-tiny">Customer</div>
                  <div class="subtitle-1 bm-ell">{{ summary.customer || "—" }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="bm-tiny">Sales · tanggal PO · mata uang</div>
                  <div class="subtitle-1 bm-ell">
                    {{ summary.sales || "—" }} · {{ formatDate(summary.poDate) }} ·
                    {{ summary.currency || "IDR" }}
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="bm-tiny">Job PPIC terhubung</div>
                  <div class="subtitle-1 bm-ell">
                    <template v-if="jobs.length">
                      <router-link
                        v-for="(j, i) in jobs"
                        :key="j.jobId"
                        :to="'/report/budget-monitoring/job/' + j.jobId"
                      >
                        {{ i ? ", " : "" }}{{ j.jobId }} · {{ j.jobName || "—" }}
                      </router-link>
                    </template>
                    <span v-else class="warning--text">belum ada job di PPIC</span>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="bm-tiny">Dihitung · dicek</div>
                  <div class="subtitle-1 bm-ell">
                    {{ formatDateTime(summary.computedAt) }} ·
                    {{ formatDateTime(summary.checkedAt) }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="4">
              <div
                class="bm-verdict"
                :style="{ borderColor: bandHex(summary.band), background: bandTint(summary.band) }"
              >
                <span class="bm-dot-lg" :style="{ background: bandHex(summary.band) }"></span>
                <div style="min-width: 0">
                  <div class="bm-cap" :style="{ color: bandHex(summary.band) }">
                    {{ verdictTitle }}
                  </div>
                  <div class="bm-verdict-value">
                    {{ formatPct(summary.ratioPercent)
                    }}<span class="bm-tiny ml-2">dari budget</span>
                  </div>
                  <div class="bm-tiny">
                    <template v-if="projected">
                      Proyeksi akhir
                      <strong :style="{ color: bandHex(summary.projectedBand) }">{{
                        formatPct(summary.projectedRatioPercent)
                      }}</strong>
                      ({{ bandName(summary.projectedBand).toLowerCase() }}) ·
                    </template>
                    <span v-if="prev"
                      >Sebelumnya {{ bandName(prev.band) }} ({{
                        formatPct(prev.ratioPercent)
                      }})</span
                    >
                    <span v-else>Belum pernah berpindah band</span>
                    <span v-if="summary.lastNotifiedAt">
                      · email terakhir {{ formatDateTime(summary.lastNotifiedAt) }}</span
                    >
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- tiga kelompok angka -->
        <v-row class="mb-1">
          <v-col cols="12" md="4">
            <v-card class="pa-4 bm-kpi bm-kpi--budget fill-height">
              <div class="bm-cap">BUDGET</div>
              <div class="bm-kpi-value mt-1">
                <template v-if="summary.band !== 'NO_BUDGET'"
                  >Rp {{ formatIdr(summary.budgetIdr) }}</template
                >
                <span v-else class="grey--text">Belum ada</span>
              </div>
              <div class="bm-tiny mt-1">
                <template v-if="summary.band !== 'NO_BUDGET'">
                  = subtotal PO {{ formatIdr(summary.subtotalIdr) }} × (1 − margin
                  {{ formatPct(summary.marginPercent) }})<br />
                  Margin dari <strong>{{ marginSourceLabel(summary.marginSource) }}</strong>
                </template>
                <template v-else>
                  Margin tidak ada di CRM (estimator / quotation kosong).
                  <a @click="showOverride = true">Isi override margin</a> agar PO ini punya budget.
                </template>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="pa-4 bm-kpi bm-kpi--usage fill-height">
              <div class="bm-cap">PEMAKAIAN</div>
              <div class="bm-kpi-value mt-1">Rp {{ formatIdr(summary.usageIdr) }}</div>
              <div class="bm-tiny mt-1 d-flex justify-space-between align-center">
                <span
                  ><span class="bm-dot" style="background: #1976d2"></span>Belanja PO supplier</span
                >
                <span class="bm-num d-flex align-center">
                  {{ formatIdr(summary.spendIdr) }}
                  <v-chip
                    x-small
                    color="blue lighten-5"
                    class="blue--text text--darken-2 ml-2 bm-chip-link"
                    @click="openBreakdown('SPEND')"
                    >{{ summary.spendPoCount }} PO</v-chip
                  >
                </span>
              </div>
              <div class="bm-tiny mt-1 d-flex justify-space-between align-center">
                <span
                  ><span class="bm-dot" style="background: #3949ab"></span>Pemakaian stok (MR)</span
                >
                <span class="bm-num d-flex align-center">
                  {{ formatIdr(summary.stockIdr) }}
                  <v-chip
                    x-small
                    :color="summary.stockMrCount ? 'indigo lighten-5' : 'grey lighten-3'"
                    :class="
                      summary.stockMrCount ? 'indigo--text ml-2 bm-chip-link' : 'grey--text ml-2'
                    "
                    @click="summary.stockMrCount && openBreakdown('STOCK')"
                    >{{ summary.stockMrCount }} MR</v-chip
                  >
                </span>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card
              v-if="projected"
              class="pa-4 bm-kpi fill-height"
              :style="{ borderTopColor: bandHex(summary.projectedBand) }"
            >
              <div class="bm-cap">PROYEKSI AKHIR</div>
              <div class="bm-kpi-value mt-1 d-flex align-center">
                {{ formatPct(summary.projectedRatioPercent) }}
                <v-chip x-small dark :color="bandColor(summary.projectedBand)" class="ml-2">{{
                  bandName(summary.projectedBand)
                }}</v-chip>
              </div>
              <div class="bm-tiny mt-1 d-flex justify-space-between align-center">
                <span class="bm-ell">Pemakaian sekarang</span>
                <span class="bm-num">{{ formatIdr(summary.usageIdr) }}</span>
              </div>
              <div class="bm-tiny mt-1 d-flex justify-space-between align-center">
                <span class="bm-ell">+ Committed (PR / PO belum approve)</span>
                <span class="bm-num d-flex align-center">
                  {{ formatIdr(summary.committedIdr) }}
                  <v-chip
                    v-if="Number(summary.committedIdr) > 0"
                    x-small
                    color="blue-grey lighten-5"
                    class="blue-grey--text text--darken-2 ml-2 bm-chip-link"
                    @click="openBreakdown('COMMITTED')"
                    >rincian</v-chip
                  >
                </span>
              </div>
              <div class="bm-tiny mt-1 d-flex justify-space-between align-center">
                <span class="bm-ell" title="Sisa BOM belum dibeli / di-MR × harga terakhir"
                  >+ Sisa BOM × harga terakhir</span
                >
                <span class="bm-num d-flex align-center">
                  {{ formatIdr(summary.projectedRemainingIdr) }}
                  <v-chip
                    x-small
                    :color="summary.bomItemCount ? 'pink lighten-5' : 'grey lighten-3'"
                    :class="
                      summary.bomItemCount
                        ? 'pink--text text--darken-4 ml-2 bm-chip-link'
                        : 'grey--text ml-2'
                    "
                    @click="summary.bomItemCount && openBreakdown('BOM')"
                    >{{ summary.bomItemCount }} item</v-chip
                  >
                </span>
              </div>
              <div class="bm-tiny mt-1">
                <span v-if="summary.bomNoPriceCount"
                  >{{ summary.bomNoPriceCount }} item tanpa harga (tidak dihitung) ·
                </span>
                <template v-if="estimated"
                  >alokasi
                  <strong class="orange--text text--darken-3"
                    >estimasi {{ Math.round(estimatedShare(summary)) }} %</strong
                  ></template
                >
                <template v-else>alokasi <strong>pasti 100 %</strong></template>
              </div>
            </v-card>
            <v-card
              v-else
              class="pa-4 bm-kpi fill-height"
              :class="estimated ? 'bm-kpi--estimate' : ''"
            >
              <div class="bm-cap">BELUM TERBUKUKAN · KEYAKINAN</div>
              <div class="bm-kpi-value mt-1 d-flex align-center">
                Rp {{ formatIdr(summary.committedIdr) }}
                <v-chip
                  v-if="Number(summary.committedIdr) > 0"
                  x-small
                  color="blue-grey lighten-5"
                  class="blue-grey--text text--darken-2 ml-2 bm-chip-link"
                  @click="openBreakdown('COMMITTED')"
                  >rincian</v-chip
                >
              </div>
              <div class="bm-tiny mt-1">
                Committed: PR belum ada PO / PO belum approve. Tidak masuk rasio.<br />
                <template v-if="estimated">
                  Alokasi
                  <strong class="orange--text text--darken-3"
                    >estimasi {{ Math.round(estimatedShare(summary)) }} %</strong
                  >
                  — job berbagi PO, dibagi proporsional.
                </template>
                <template v-else
                  >Alokasi <strong>pasti 100 %</strong> — semua belanja jelas milik PO
                  ini.</template
                >
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- bar posisi -->
        <v-card v-if="summary.band !== 'NO_BUDGET'" class="pa-4 mb-4">
          <div class="bm-tiny d-flex justify-space-between">
            <span>0 %</span><span>{{ yellowFrom }} % kuning</span><span>{{ redFrom }} % merah</span
            ><span>{{ barMax }} %</span>
          </div>
          <div class="bm-bigbar my-2">
            <div
              class="bm-bigbar-seg"
              :style="{ left: 0, width: pctOfBar(spendPct) + '%', background: '#1976d2' }"
            ></div>
            <div
              class="bm-bigbar-seg"
              :style="{
                left: pctOfBar(spendPct) + '%',
                width: pctOfBar(stockPct) + '%',
                background: '#3949ab',
              }"
            ></div>
            <div
              v-if="projected && projectionPct > 0"
              class="bm-bigbar-seg bm-bigbar-seg--hatched"
              :style="{
                left: pctOfBar(spendPct + stockPct) + '%',
                width: pctOfBar(projectionPct) + '%',
              }"
              :title="
                'Proyeksi: committed + sisa BOM = ' + formatPct(projectionPct) + ' dari budget'
              "
            ></div>
            <div
              class="bm-bigbar-mark"
              :style="{ left: pctOfBar(yellowFrom) + '%', background: '#fb8c00' }"
            ></div>
            <div
              class="bm-bigbar-mark"
              :style="{ left: pctOfBar(redFrom) + '%', background: '#e53935' }"
            ></div>
          </div>
          <div class="bm-tiny d-flex flex-wrap" style="gap: 20px">
            <span
              ><span class="bm-dot" style="background: #1976d2"></span>Belanja
              {{ formatPct(spendPct) }} dari budget</span
            >
            <span
              ><span class="bm-dot" style="background: #3949ab"></span>Stok
              {{ formatPct(stockPct) }}</span
            >
            <span v-if="projected"
              ><span class="bm-dot bm-dot--hatched"></span>Proyeksi +{{
                formatPct(projectionPct)
              }}
              (committed + sisa BOM)</span
            >
            <span>
              Sisa budget:
              <strong :class="overBudget ? 'error--text' : ''">{{ remainingLabel }}</strong>
              <span v-if="overBudget">(melebihi)</span>
            </span>
          </div>
        </v-card>

        <v-row align="start">
          <v-col cols="12" md="7">
            <!-- per job -->
            <v-card class="mb-4">
              <div class="bm-sec">
                <div>
                  <div class="bm-sec-title">Pemakaian per job</div>
                  <div class="bm-sec-sub">
                    Job PPIC yang terhubung ke PO ini, bagian budget, dan pemakaiannya.
                  </div>
                </div>
              </div>
              <v-simple-table dense class="bm-table bm-fixed">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th class="text-right" style="width: 116px">Bagian budget</th>
                    <th class="text-right" style="width: 112px">Belanja PO</th>
                    <th class="text-right" style="width: 108px">Stok</th>
                    <th class="text-right" style="width: 112px">Pemakaian</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="j in jobs" :key="j.jobId">
                    <td>
                      <div class="bm-cell">
                        <router-link
                          :to="'/report/budget-monitoring/job/' + j.jobId"
                          class="font-weight-medium text-decoration-none"
                          >{{ j.jobId }} · {{ j.jobName || "—" }}</router-link
                        >
                        <span class="bm-tiny">
                          share
                          {{
                            j.share !== null && j.share !== undefined
                              ? Math.round(Number(j.share) * 100) + " %"
                              : "—"
                          }}
                          · {{ methodLabel(j.method)
                          }}{{ jobs.length === 1 ? " · job tunggal" : "" }}
                        </span>
                      </div>
                    </td>
                    <td class="text-right bm-num">{{ formatIdr(j.budgetIdr) }}</td>
                    <td class="text-right">
                      <div class="bm-cell bm-cell--end">
                        <span class="bm-num">{{ formatIdr(j.spendIdr) }}</span>
                        <v-chip
                          x-small
                          color="blue lighten-5"
                          class="blue--text text--darken-2 bm-chip-link"
                          @click="openBreakdown('SPEND', j)"
                          >{{ j.spendPoCount }}</v-chip
                        >
                      </div>
                    </td>
                    <td class="text-right">
                      <div class="bm-cell bm-cell--end">
                        <span class="bm-num">{{ formatIdr(j.stockIdr) }}</span>
                        <v-chip
                          x-small
                          :color="j.stockMrCount ? 'indigo lighten-5' : 'grey lighten-3'"
                          :class="j.stockMrCount ? 'indigo--text bm-chip-link' : 'grey--text'"
                          @click="j.stockMrCount && openBreakdown('STOCK', j)"
                          >{{ j.stockMrCount }}</v-chip
                        >
                      </div>
                    </td>
                    <td class="text-right bm-num font-weight-medium">
                      {{ formatIdr(j.usageIdr) }}
                    </td>
                  </tr>
                  <tr v-if="!jobs.length">
                    <td colspan="5" class="text-center grey--text py-6">
                      Belum ada job PPIC yang terhubung ke PO ini.
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>

            <!-- item PO customer -->
            <v-card>
              <div class="bm-sec">
                <div>
                  <div class="bm-sec-title">Item PO customer</div>
                  <div class="bm-sec-sub">
                    Barang yang dijual di PO ini menurut CRM. Tautan ke panel code job menentukan
                    bagian budget; belanja &amp; stok = yang dialokasikan lewat panel code itu.
                  </div>
                </div>
                <v-chip
                  v-if="Number(summary.unlinkedItemShare) > 0"
                  x-small
                  color="orange lighten-5"
                  class="orange--text text--darken-3"
                >
                  {{ formatPct(summary.unlinkedItemShare * 100) }} nilai item belum terhubung ke job
                </v-chip>
                <v-chip
                  v-else-if="items.length"
                  x-small
                  color="green lighten-5"
                  class="green--text text--darken-3"
                  >Semua item terhubung</v-chip
                >
              </div>
              <v-simple-table dense class="bm-table bm-fixed">
                <thead>
                  <tr>
                    <th>Item PO customer</th>
                    <th class="text-right" style="width: 118px">Nilai (IDR)</th>
                    <th style="width: 196px">Panel code · job</th>
                    <th class="text-right" style="width: 108px">Belanja PO</th>
                    <th class="text-right" style="width: 96px">Stok</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="it in items" :key="it.poProductId">
                    <td>
                      <div class="bm-cell">
                        <span class="font-weight-medium" :title="it.partNumber">{{
                          it.partNumber || (it.productId ? "#" + it.productId : "—")
                        }}</span>
                        <span class="bm-tiny" :title="it.partName">{{ it.partName || "—" }}</span>
                      </div>
                    </td>
                    <td class="text-right bm-num">{{ formatIdr(it.totalPrice) }}</td>
                    <td>
                      <div v-if="(it.panelCodes || []).length" class="bm-cell">
                        <router-link
                          v-for="p in it.panelCodes.slice(0, 2)"
                          :key="p.id"
                          :to="'/report/budget-monitoring/job/' + p.jobId"
                          class="text-decoration-none d-block"
                          style="max-width: 100%"
                        >
                          <v-chip
                            x-small
                            color="green lighten-5"
                            class="green--text text--darken-3 bm-chip-ell"
                            :title="p.name"
                            >{{ p.name || "#" + p.id }}</v-chip
                          >
                        </router-link>
                        <span class="bm-tiny" :title="panelJobsCopy(it)">
                          <template v-if="it.panelCodes.length > 2"
                            >+{{ it.panelCodes.length - 2 }} panel code lagi · </template
                          >{{ panelJobsCopy(it) }}
                        </span>
                      </div>
                      <div v-else class="bm-cell">
                        <v-chip x-small color="grey lighten-3" class="align-self-start"
                          >belum terhubung</v-chip
                        >
                        <span class="bm-tiny">{{
                          jobs.length > 1
                            ? "dibagi rata antar job · isi panel code di PPIC"
                            : "isi panel code di PPIC"
                        }}</span>
                      </div>
                    </td>
                    <td class="text-right bm-num">
                      <span :class="{ 'grey--text': !(it.panelCodes || []).length }">{{
                        formatIdr(it.spendIdr)
                      }}</span>
                    </td>
                    <td class="text-right bm-num">
                      <span :class="{ 'grey--text': !(it.panelCodes || []).length }">{{
                        formatIdr(it.stockIdr)
                      }}</span>
                    </td>
                  </tr>
                  <tr v-if="!items.length">
                    <td colspan="5" class="text-center grey--text py-6">
                      Tidak ada item di PO CRM.
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </v-col>

          <v-col cols="12" md="5">
            <!-- riwayat rasio -->
            <v-card class="mb-4">
              <div class="bm-sec">
                <div>
                  <div class="bm-sec-title">Perjalanan rasio</div>
                  <div class="bm-sec-sub">Setiap titik = perhitungan yang mengubah angka.</div>
                </div>
                <v-btn-toggle
                  v-model="historyDays"
                  mandatory
                  dense
                  color="primary"
                  @change="loadHistory"
                >
                  <v-btn :value="90" x-small text>90 hari</v-btn>
                  <v-btn :value="365" x-small text>365 hari</v-btn>
                </v-btn-toggle>
              </div>
              <div class="px-5 py-3">
                <budget-trend-chart
                  :history="history"
                  :yellow="yellowFrom"
                  :red="redFrom"
                ></budget-trend-chart>
              </div>
            </v-card>

            <!-- notifikasi -->
            <v-card>
              <div class="bm-sec">
                <div>
                  <div class="bm-sec-title">Notifikasi email</div>
                  <div class="bm-sec-sub">
                    Dikirim hanya saat band berubah dan isinya berbeda dari email sebelumnya.
                    Penerima diatur di Setting.
                  </div>
                </div>
              </div>
              <v-simple-table dense class="bm-fixed bm-scroll">
                <thead>
                  <tr>
                    <th style="width: 150px">Waktu</th>
                    <th>Perubahan</th>
                    <th style="width: 110px">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in alerts" :key="a.id">
                    <td class="bm-tiny">{{ formatDateTime(a.sentAt) }}</td>
                    <td>
                      {{ bandName(a.fromBand) }} → {{ bandName(a.toBand) }}
                      <div v-if="a.recipients" class="bm-tiny bm-ell" :title="a.recipients">
                        {{ a.recipients }}
                      </div>
                    </td>
                    <td>
                      <v-chip
                        x-small
                        :color="alertColor(a.status)"
                        :class="alertTextClass(a.status)"
                        :title="a.error || ''"
                        >{{ alertLabel(a.status) }}</v-chip
                      >
                    </td>
                  </tr>
                  <tr v-if="!alerts.length">
                    <td colspan="3" class="text-center grey--text py-6">
                      Belum ada perpindahan band yang tercatat.
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div
                v-if="alerts.some(a => a.status === 'SKIPPED' || a.status === 'DUPLICATE')"
                class="bm-tiny px-5 pb-3 pt-2"
              >
                <span v-if="alerts.some(a => a.status === 'SKIPPED')"
                  >Dilewati = belum ada penerima untuk band itu saat kejadian.
                </span>
                <span v-if="alerts.some(a => a.status === 'DUPLICATE')"
                  >Isi sama = email yang sama sudah pernah terkirim, tidak dikirim ulang.</span
                >
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-col>

    <budget-spend-breakdown v-model="showBreakdown" :context="breakdown"></budget-spend-breakdown>
    <budget-override-dialog
      v-model="showOverride"
      :po="summary"
      @saved="load"
    ></budget-override-dialog>
    <budget-recompute-dialog
      v-model="confirmRecompute"
      scope="po"
      :subject="'PO ' + (summary.poNumber || '#' + crmPoId)"
      :last-run-at="formatDateTime(summary.computedAt)"
      :loading="recomputing"
      @confirm="recompute"
    ></budget-recompute-dialog>
  </v-row>
</template>

<script>
import fileDownload from "js-file-download";
import BudgetSpendBreakdown from "@/components/budget/BudgetSpendBreakdown.vue";
import BudgetOverrideDialog from "@/components/budget/BudgetOverrideDialog.vue";
import BudgetRecomputeDialog from "@/components/budget/BudgetRecomputeDialog.vue";
import BudgetTrendChart from "@/components/budget/BudgetTrendChart.vue";
import EmptyState from "@/components/budget/EmptyState.vue";
import {
  bandColor,
  bandHex,
  bandName,
  bandTint,
  estimatedShare,
  hasProjection,
  formatDate,
  formatDateTime,
  formatIdr,
  formatPct,
  marginSourceLabel,
  methodLabel,
  previousBand,
} from "@/utils/budgetMonitor";
import "@/components/budget/budget-monitor.css";

export default {
  name: "budget-monitoring-po-detail",
  components: {
    BudgetSpendBreakdown,
    BudgetOverrideDialog,
    BudgetRecomputeDialog,
    BudgetTrendChart,
    EmptyState,
  },
  data: () => ({
    loading: false,
    recomputing: false,
    exporting: false,
    confirmRecompute: false,
    detail: null,
    history: [],
    historyDays: 90,
    showBreakdown: false,
    breakdown: { bucket: "SPEND", expectedAmount: 0 },
    showOverride: false,
  }),
  computed: {
    crmPoId() {
      return Number(this.$route.params.crmPoId);
    },
    summary() {
      return (this.detail && this.detail.summary) || {};
    },
    jobs() {
      return (this.detail && this.detail.jobs) || [];
    },
    items() {
      return (this.detail && this.detail.items) || [];
    },
    alerts() {
      return (this.detail && this.detail.alerts) || [];
    },
    setting() {
      return this.$store.state.budgetMonitor.setting || {};
    },
    yellowFrom() {
      return Number(this.setting.yellowFrom || 80);
    },
    redFrom() {
      return Number(this.setting.redFrom || 100);
    },
    barMax() {
      // scale ends 10 % above the (projected) ratio, but never beyond 300 %: a 21 000 % ratio still shows the bands
      const top =
        Math.ceil(
          Math.max(
            Number(this.summary.ratioPercent) || 0,
            Number(this.summary.projectedRatioPercent) || 0
          ) / 10
        ) *
          10 +
        10;
      return Math.min(300, Math.max(this.redFrom + 20, top));
    },
    spendPct() {
      const b = Number(this.summary.budgetIdr || 0);
      return b > 0 ? (Number(this.summary.spendIdr || 0) / b) * 100 : 0;
    },
    stockPct() {
      const b = Number(this.summary.budgetIdr || 0);
      return b > 0 ? (Number(this.summary.stockIdr || 0) / b) * 100 : 0;
    },
    remaining() {
      return Number(this.summary.budgetIdr || 0) - Number(this.summary.usageIdr || 0);
    },
    /** the run produced a projected ratio (setting on, PO has a budget) */
    projected() {
      return hasProjection(this.summary);
    },
    /** committed + BOM remainder as % of budget: the hatched bar segment */
    projectionPct() {
      const b = Number(this.summary.budgetIdr || 0);
      if (b <= 0) return 0;
      return (
        ((Number(this.summary.committedIdr || 0) +
          Number(this.summary.projectedRemainingIdr || 0)) /
          b) *
        100
      );
    },
    overBudget() {
      return this.remaining < 0;
    },
    remainingLabel() {
      return (this.overBudget ? "−" : "") + formatIdr(Math.abs(this.remaining));
    },
    estimated() {
      return estimatedShare(this.summary) > 0;
    },
    prev() {
      return previousBand(this.history);
    },
    verdictTitle() {
      const b = this.summary.band;
      return b === "RED"
        ? "MERAH · MELEBIHI BUDGET"
        : b === "YELLOW"
        ? "KUNING · MENDEKATI BATAS"
        : b === "GREEN"
        ? "HIJAU · DALAM BUDGET"
        : "TANPA BUDGET";
    },
  },
  watch: {
    "$route.params.crmPoId"() {
      this.load();
    },
  },
  created() {
    this.load();
    if (!this.$store.state.budgetMonitor.setting) {
      this.$store.dispatch("budgetMonitor/fetchSetting").catch(() => {});
    }
  },
  methods: {
    formatIdr,
    formatPct,
    formatDate,
    formatDateTime,
    bandColor,
    bandHex,
    bandName,
    bandTint,
    estimatedShare,
    marginSourceLabel,
    methodLabel,
    pctOfBar(pct) {
      return Math.max(0, Math.min(100, ((Number(pct) || 0) / this.barMax) * 100));
    },
    panelJobsCopy(it) {
      const jobs = [];
      (it.panelCodes || []).forEach(p => {
        const label = `job ${p.jobId}${p.jobName ? " · " + p.jobName : ""}`;
        if (!jobs.includes(label)) jobs.push(label);
      });
      return jobs.join(", ");
    },
    alertLabel(status) {
      return status === "SENT"
        ? "Terkirim"
        : status === "FAILED"
        ? "Gagal"
        : status === "DUPLICATE"
        ? "Isi sama"
        : "Dilewati";
    },
    alertColor(status) {
      return status === "SENT"
        ? "green lighten-5"
        : status === "FAILED"
        ? "red lighten-5"
        : "grey lighten-3";
    },
    alertTextClass(status) {
      return status === "SENT"
        ? "green--text text--darken-3"
        : status === "FAILED"
        ? "red--text text--darken-3"
        : "";
    },
    async load() {
      this.loading = true;
      try {
        this.detail = await this.$store.dispatch("budgetMonitor/fetchPoDetail", this.crmPoId);
        await this.loadHistory();
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.detail = null;
        if (status !== 404)
          this.$store.dispatch("toast", { type: "error", message: "Gagal memuat detail PO" });
      } finally {
        this.loading = false;
      }
    },
    async loadHistory() {
      try {
        this.history = await this.$store.dispatch("budgetMonitor/fetchPoHistory", {
          crmPoId: this.crmPoId,
          days: this.historyDays,
        });
      } catch (e) {
        this.history = [];
      }
    },
    openBreakdown(bucket, job) {
      const src = job || this.summary;
      this.breakdown = {
        crmPoId: this.crmPoId,
        jobId: job ? job.jobId : undefined,
        jobName: job ? job.jobName : undefined,
        jobs: this.jobs,
        bucket,
        expectedAmount:
          bucket === "STOCK"
            ? src.stockIdr
            : bucket === "COMMITTED"
            ? src.committedIdr
            : bucket === "BOM"
            ? job
              ? src.bomIdr
              : src.projectedRemainingIdr
            : src.spendIdr,
        poNumber: this.summary.poNumber,
        estimatedPercent: this.summary.estimatedPercent,
        budgetIdr: this.summary.budgetIdr,
        usageIdr: this.summary.usageIdr,
      };
      this.showBreakdown = true;
    },
    async recompute() {
      this.recomputing = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/recomputePo", this.crmPoId);
        this.confirmRecompute = false;
        const n = (res && res.errors && res.errors.length) || 0;
        this.$store.dispatch("toast", {
          type: res && res.status === "DONE" ? "success" : "warning",
          message:
            res && res.status === "DONE"
              ? "PO dihitung ulang."
              : `Perhitungan ${res && res.status} (${n} catatan)`,
        });
        await this.load();
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.confirmRecompute = false;
        this.$store.dispatch("toast", {
          type: status === 409 ? "warning" : "error",
          message:
            status === 409
              ? "Perhitungan lain sedang berjalan — tunggu selesai."
              : "Gagal menghitung ulang",
        });
      } finally {
        this.recomputing = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/exportPo", this.crmPoId);
        fileDownload(res.data, `budget-${this.summary.poNumber || this.crmPoId}.xlsx`);
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal mengekspor" });
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.bm-back {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  text-decoration: none;
  white-space: nowrap;
}
.bm-verdict {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 4px;
  border: thin solid;
  min-width: 0;
}
.bm-verdict-value {
  font-size: 22px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.bm-dot,
.bm-dot-lg {
  display: inline-block;
  border-radius: 50%;
  flex: none;
}
.bm-dot {
  width: 8px;
  height: 8px;
  margin-right: 6px;
  vertical-align: middle;
}
.bm-dot-lg {
  width: 12px;
  height: 12px;
}
.bm-bigbar {
  position: relative;
  height: 14px;
  border-radius: 7px;
  background: #eeeeee;
  overflow: hidden;
}
.bm-bigbar-seg {
  position: absolute;
  top: 0;
  bottom: 0;
}
.bm-bigbar-mark {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
}
/* projection (committed + sisa BOM) on the position bar */
.bm-bigbar-seg--hatched {
  background: repeating-linear-gradient(45deg, #b71c1c 0 3px, #ffcdd2 3px 6px);
}
.bm-dot--hatched {
  width: 14px;
  height: 8px;
  border-radius: 2px;
  background: repeating-linear-gradient(45deg, #b71c1c 0 3px, #ffcdd2 3px 6px);
}
</style>
